// resolveKbdPlatform — the boundary reader that decides which platform
// register Kbd shortcut hints render in (ui-design-synthesis §2).
//
// AUDIENCE NOTE (AI register): this is the ONE place the UI derives the
// shortcut platform. Precedence, in order:
//
//   1. A minimal navigator sniff. Only the mac-vs-not distinction matters
//      visually (the "windows" and "linux" registers render identically as
//      text caps), so any non-Apple environment honestly reads as "windows".
//   2. "mac" outside a browser context (tests/SSR) — the pre-platform visual
//      status quo.
//
// PURITY: this is a boundary reader, not a component
// concern. Impure hosts (AppShellHost and the shell components it mounts)
// call it — typically once — and feed the result to pure components as the
// `platform` prop (Kbd et al.). Pure components must NOT import this module.

import type { KbdPlatform } from "../components/Kbd/Kbd.js";

// True when the browser environment self-describes as Apple. Reads the
// legacy-but-universal `navigator.platform` first, then the UA string —
// both untyped world input, both only trusted for this one cosmetic choice.
const navigatorIsMac = (): boolean => {
    if (typeof navigator === "undefined") return false;
    return /mac|iphone|ipad/i.test(
        `${navigator.platform} ${navigator.userAgent}`,
    );
};

export const resolveKbdPlatform = (): KbdPlatform => {
    if (typeof navigator === "undefined") return "mac";
    return navigatorIsMac() ? "mac" : "windows";
};
